import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ThreatIntelligence = ({ events }) => {
  const [threatData, setThreatData] = useState({
    knownThreats: [],
    attackPatterns: [],
    riskScore: 0
  });

  useEffect(() => {
    analyzeThreatData();
  }, [events]);

  const analyzeThreatData = () => {
    // Analyze events for threat patterns
    const eventTypes = {};
    const ipAddresses = {};
    
    events?.forEach(event => {
      eventTypes[event?.event_type] = (eventTypes?.[event?.event_type] || 0) + 1;
      if (event?.ip_address) {
        ipAddresses[event?.ip_address] = (ipAddresses?.[event?.ip_address] || 0) + 1;
      }
    });

    // Mock threat intelligence data
    const knownThreats = [
      {
        id: 1,
        name: 'Brute Force Attack Pattern',
        severity: 'critical',
        description: 'Multiple failed login attempts detected from same IP',
        matchedEvents: eventTypes?.failed_login || 0,
        mitigation: 'Implement rate limiting and IP blocking'
      },
      {
        id: 2,
        name: 'Suspicious Access Pattern',
        severity: 'warning',
        description: 'Unusual access times and locations detected',
        matchedEvents: eventTypes?.suspicious_activity || 0,
        mitigation: 'Enable multi-factor authentication'
      },
      {
        id: 3,
        name: 'Privilege Escalation Attempt',
        severity: 'error',
        description: 'Unauthorized privilege elevation detected',
        matchedEvents: eventTypes?.privilege_escalation || 0,
        mitigation: 'Review and restrict role permissions'
      }
    ];

    const attackPatterns = [
      { pattern: 'Failed Login Attempts', count: eventTypes?.failed_login || 0, trend: 'up' },
      { pattern: 'Unauthorized Access', count: eventTypes?.unauthorized_access || 0, trend: 'stable' },
      { pattern: 'Data Breach Attempts', count: eventTypes?.data_breach_attempt || 0, trend: 'down' }
    ];

    const riskScore = Math.min(100, (events?.length || 0) * 5 + (eventTypes?.critical || 0) * 15);

    setThreatData({ knownThreats, attackPatterns, riskScore });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'var(--color-error)',
      error: 'var(--color-error)',
      warning: 'var(--color-warning)',
      info: 'var(--color-info)'
    };
    return colors?.[severity] || 'var(--color-muted)';
  };

  const getRiskLevel = (score) => {
    if (score >= 75) return { label: 'Critical', color: 'var(--color-error)' };
    if (score >= 50) return { label: 'High', color: 'var(--color-warning)' };
    if (score >= 25) return { label: 'Medium', color: 'var(--color-info)' };
    return { label: 'Low', color: 'var(--color-success)' };
  };

  const riskLevel = getRiskLevel(threatData?.riskScore);

  return (
    <div className="space-y-6">
      {/* Risk Score Card */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-heading font-semibold text-foreground">
            Threat Risk Assessment
          </h3>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} className="mr-2" />
            Export Report
          </Button>
        </div>

        <div className="flex items-center gap-8">
          <div className="flex-1">
            <div className="relative w-full h-4 bg-muted rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full transition-all duration-500"
                style={{
                  width: `${threatData?.riskScore}%`,
                  backgroundColor: riskLevel?.color
                }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-muted-foreground">Risk Score</span>
              <span className="text-2xl font-heading font-bold" style={{ color: riskLevel?.color }}>
                {threatData?.riskScore}/100
              </span>
            </div>
          </div>
          <div
            className="px-6 py-3 rounded-lg font-medium"
            style={{
              backgroundColor: `${riskLevel?.color}15`,
              color: riskLevel?.color
            }}
          >
            {riskLevel?.label} Risk
          </div>
        </div>
      </div>

      {/* Known Threats */}
      <div className="card">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Known Threat Patterns
        </h3>
        <div className="space-y-4">
          {threatData?.knownThreats?.map((threat) => (
            <div key={threat?.id} className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon name="Shield" size={18} color={getSeverityColor(threat?.severity)} />
                  <h4 className="text-sm font-medium text-foreground">{threat?.name}</h4>
                </div>
                <span
                  className="px-2 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: `${getSeverityColor(threat?.severity)}15`,
                    color: getSeverityColor(threat?.severity)
                  }}
                >
                  {threat?.severity}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-2">{threat?.description}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Matched Events: <span className="font-medium text-foreground">{threat?.matchedEvents}</span>
                </span>
                <span className="text-primary">Mitigation: {threat?.mitigation}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Attack Patterns */}
      <div className="card">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
          Attack Pattern Analysis
        </h3>
        <div className="space-y-3">
          {threatData?.attackPatterns?.map((pattern, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="Activity" size={18} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{pattern?.pattern}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-foreground">{pattern?.count} events</span>
                <Icon
                  name={pattern?.trend === 'up' ? 'TrendingUp' : pattern?.trend === 'down' ? 'TrendingDown' : 'Minus'}
                  size={16}
                  color={pattern?.trend === 'up' ? 'var(--color-error)' : pattern?.trend === 'down' ? 'var(--color-success)' : 'var(--color-muted)'}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ThreatIntelligence;